import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * usePagination — pure client-side pagination helper (NOT a real hook, no useMemo).
 * Named use* for ergonomic call-sites but safe to call after early returns.
 */
export function usePagination(list, pageSize, page, setPage) {
  const pageCount = Math.max(1, Math.ceil((list?.length || 0) / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;
  const paged = (list || []).slice(start, start + pageSize);
  return { pageCount, page: safePage, setPage, paged };
}

/**
 * Pagination — controlled prev/next + numeric buttons. Testid-friendly.
 */
export function Pagination({ page, pageCount, onChange, total, pageSize, testidPrefix = "pg" }) {
  if (pageCount <= 1 && !total) return null;
  const nums = getPageNumbers(page, pageCount);
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs">
      <div className="text-stone-500" data-testid={`${testidPrefix}-summary`}>
        {typeof total === "number" ? `${total} total · ` : ""}Page {page} of {pageCount}
        {pageSize ? ` · ${pageSize}/page` : ""}
      </div>
      <div className="flex items-center gap-1 flex-wrap justify-end">
        <Button
          size="sm"
          variant="outline"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          data-testid={`${testidPrefix}-prev`}
          className="h-8 rounded-full"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Prev</span>
        </Button>
        <span className="sm:hidden text-stone-500 px-1" data-testid={`${testidPrefix}-compact`}>{page}/{pageCount}</span>
        <span className="hidden sm:contents">
        {nums.map((n, i) =>
          n === "…" ? (
            <span key={`e-${i}`} className="px-2 text-stone-400">…</span>
          ) : (
            <Button
              key={n}
              size="sm"
              variant={n === page ? "default" : "outline"}
              onClick={() => onChange(n)}
              data-testid={`${testidPrefix}-page-${n}`}
              className={`h-8 min-w-8 rounded-full ${n === page ? "bg-stoneWarm text-white hover:bg-stoneWarm/90" : ""}`}
            >
              {n}
            </Button>
          )
        )}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={page >= pageCount}
          onClick={() => onChange(page + 1)}
          data-testid={`${testidPrefix}-next`}
          className="h-8 rounded-full"
        >
          <span className="hidden sm:inline">Next</span> <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function getPageNumbers(page, pageCount) {
  const out = [];
  if (pageCount <= 7) {
    for (let i = 1; i <= pageCount; i++) out.push(i);
    return out;
  }
  out.push(1);
  if (page > 3) out.push("…");
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  for (let i = start; i <= end; i++) out.push(i);
  if (page < pageCount - 2) out.push("…");
  out.push(pageCount);
  return out;
}
